// src/components/EFIchart.js
import React, { useState, useEffect, useMemo } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { Box, CircularProgress, Alert } from '@mui/material';
import PropTypes from 'prop-types';
import { categoryColors } from '../../config/dashboardConfig';

const EFIchart = ({ modelName = 'M1', country = 'Greece', method = 'Aggregated', days = 3, efiData }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Use darker versions of the category colors for EFI DOWN
  const darkerCategoryColors = {
    peak_peak: '#7a63cc',          // Darker purple for EFI DOWN
    off_peak_peak: '#d94d00',      // Darker orange for EFI DOWN
    peak_off_peak: '#9a3183',      // Darker pinkish for EFI DOWN
    off_off: '#4365ca',            // Darker blue for EFI DOWN
  };

  // Helper function to format hour labels
  const formatHourLabel = (hour) => {
    const formattedHour = `${hour.toString().padStart(2, '0')}:00`;
    return formattedHour;
  };

  useEffect(() => {
    // Update loading state based on whether efiData is available
    if (efiData && efiData.efiResultsAllDays && efiData.hoursStatsAllDays && efiData.efiDescriptions) {
      setLoading(false);
      setError(null);
    } else if (efiData === null || efiData === undefined) {
      setLoading(true);
      setError(null);
    } else {
      setLoading(false);
      setError('EFI data is incomplete or invalid');
    }
  }, [efiData]);

  // Move useMemo before any returns
  const options = useMemo(() => {
    // If data is not ready, return default options
    if (
      loading ||
      error ||
      !efiData ||
      !efiData.efiResultsAllDays?.length ||
      !efiData.hoursStatsAllDays?.length ||
      !efiData.efiDescriptions
    ) {
      return {};
    }

    const { efiResultsAllDays, hoursStatsAllDays, efiDescriptions } = efiData;

    // Initialize arrays for categories and data series
    const totalHours = days * 24;
    const categories = Array.from({ length: totalHours }, (_, i) => {
      const hour = i % 24;
      return formatHourLabel(hour);
    });
    const dayNumbers = Array.from({ length: totalHours }, (_, i) => Math.floor(i / 24) + 1);

    const dayPlotLines = Array.from({ length: days }, (_, i) => ({
      color: 'gray', // Customize the plot line color if needed
      width: 1,
      value: i * 24 - 0.5, // Position plot line at each day boundary
      dashStyle: 'Dash',
      label: {
        text: `Day ${i + 1}`,
        align: 'center',
        verticalAlign: 'top',
        // y: -10, // Adjust distance from the top of the line as needed
        x: 30,
        style: {
          fontSize: '15px',
          color: 'gray',
        },
        rotation: 0,
      },
    }));

    const efiUpSeries = Array(totalHours).fill(0);
    const efiDownSeries = Array(totalHours).fill(0);
    const barColorsUp = Array(totalHours).fill('gray');
    const barColorsDown = Array(totalHours).fill('gray');

    // Loop over each day and process data
    efiResultsAllDays.forEach((efiDayData, dayIndex) => {
      //const day = dayIndex + 1;
      const hoursDataForDay = hoursStatsAllDays[dayIndex];

      // Map each hour to its flexibility category and assign EFI values
      Object.keys(hoursDataForDay).forEach((categoryKey) => {
        if (categoryKey === 'date' || categoryKey === 'period_descriptions') return;

        // Skip any keys that don't end with '_hours' to avoid processing non-category fields like 'predicted_classes'
        if (!categoryKey.endsWith('_hours')) {
          //console.warn(`Skipping non-category key: ${categoryKey}`);
          return;
        }

        // categoryKey: 'peak_peak_hours', etc.
        const category = categoryKey.replace('_hours', '').toLowerCase(); // e.g., 'peak_peak'

        const hours = hoursDataForDay[categoryKey]; // array of hours for this category
        const efiValues = efiDayData.efi_results[`EFI_${categoryKey}`]; // [EFI_UP, EFI_DOWN]

        if (!efiValues || efiValues.length < 2) {
          console.warn(`EFI values missing for category ${categoryKey}`);
          return;
        }

        // Additional safety check to ensure hours is an array
        if (!Array.isArray(hours)) {
          //console.warn(`Hours data is not an array for category ${categoryKey}:`, hours);
          return;
        }

        const [efiUp, efiDown] = efiValues;

        hours.forEach((hour) => {
          // Ensure hour is integer between 0-23
          const normalizedHour = hour % 24;

          // Calculate the index in the arrays
          const index = dayIndex * 24 + normalizedHour;

          // Assign EFI_UP and EFI_DOWN to the hour
          efiUpSeries[index] = efiUp;
          efiDownSeries[index] = efiDown;

          // Assign color based on category
          barColorsUp[index] = categoryColors[category] || 'gray';
          barColorsDown[index] = darkerCategoryColors[category] || 'gray';
        });
      });
    });

    // Formatter functions
    // const xAxisFormatter = function () {
    //   const index = this.pos;
    //   const day = dayNumbers[index];
    //   return `${this.value}<br/>Day ${day}`;
    // };

    const tooltipFormatter = function () {
      const hour = this.x;
      const index = this.points[0].point.index;
      const day = dayNumbers[index];
      const efiUpPoint = this.points.find((p) => p.series.name === 'EFI UP');
      const efiDownPoint = this.points.find((p) => p.series.name === 'EFI DOWN');

      const efiUpValue = efiUpPoint ? efiUpPoint.y : 0;
      const efiDownValue = efiDownPoint ? Math.abs(efiDownPoint.y) : 0;
      const efiDifference = Math.abs(efiUpValue - efiDownValue); // Calculate EFI difference

      return `
        <b>${hour} (Day ${day})</b><br/><br/>
        <b>EFI UP:</b> ${efiUpValue}<br/>
        <b>EFI DOWN:</b> ${efiDownValue}<br/>
        <b>EFI Difference:</b> ${efiDifference} <!-- Add EFI Difference -->
      `;
    };

    // Define Highcharts options
    return {
      chart: {
        type: 'column',
      },
      title: {
        text: null,
      },
      xAxis: {
        categories: categories,
        crosshair: true,
        labels: {
          rotation: -45,
          style: {
            fontSize: '10px',
          },
        },
        plotLines: dayPlotLines, // Add plot lines here
      },
      yAxis: {
        gridLineWidth: 0,
        title: {
          text: 'EFI Values',
        },
        labels: {
          formatter: function () {
            return Math.abs(this.value);
          },
        },
      },
      tooltip: {
        shared: true,
        useHTML: true,
        formatter: tooltipFormatter,
      },
      plotOptions: {
        column: {
          grouping: false,
          shadow: false,
          borderWidth: 0,
        },
      },
      series: [
        {
          name: 'EFI UP',
          data: efiUpSeries.map((value, index) => ({
            y: value,
            color: barColorsUp[index],
          })),
          tooltip: {
            valueSuffix: '',
          },
        },
        {
          name: 'EFI DOWN',
          data: efiDownSeries.map((value, index) => ({
            y: -value, // Negative value for downward bar
            color: barColorsDown[index],
          })),
          tooltip: {
            valueSuffix: '',
          },
        },
      ],
      credits: {
        enabled: false,
      },
      legend: {
        enabled: false, // We'll add a custom legend below
      },
      accessibility: {
        enabled: false
      }
    };
  }, [
    loading,
    error,
    efiData,
    categoryColors,
    days,
  ]);

  // Now, conditional returns
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!efiData || !efiData.efiResultsAllDays?.length || !efiData.hoursStatsAllDays?.length || !efiData.efiDescriptions) {
    return null;
  }

  // Custom Legend
  // const renderLegend = () => {
  //   const uniqueCategories = Object.keys(categoryColors);
  //   return (
  //     <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
  //       {uniqueCategories.map((cat) => (
  //         <Box key={cat} sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
  //           <Box
  //             sx={{
  //               width: 16,
  //               height: 16,
  //               backgroundColor: categoryColors[cat],
  //               mr: 1,
  //             }}
  //           />
  //           <Typography variant="body2">
  //             {cat
  //               .split('_')
  //               .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
  //               .join(' ')}
  //           </Typography>
  //         </Box>
  //       ))}
  //     </Box>
  //   );
  // };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%' }}>
      <Box sx={{ flexGrow: 1, height: '80%', width: '100%' }}>
        <HighchartsReact
          highcharts={Highcharts}
          options={options}
          containerProps={{ style: { height: '100%', width: '100%' } }}
        />
      </Box>
      {/* <Box sx={{ mt: 2, height: '20%' }}>
        {renderLegend()}
      </Box> */}
    </Box>
  );
};

EFIchart.propTypes = {
  modelName: PropTypes.string,
  country: PropTypes.string,
  method: PropTypes.string,
  days: PropTypes.number,
  efiData: PropTypes.shape({
    efiResultsAllDays: PropTypes.array,
    hoursStatsAllDays: PropTypes.array,
    efiDescriptions: PropTypes.object,
  }),
};

export default EFIchart;
